import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import DeleteModal from "components/DeleteModal/DeleteModal";
import { useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { adminGetAllOwners, deleteUser, disableSendMessages } from "redux/features/auth/authSlice";
import Input from "shared/Input/Input";
import DashboardMenu from "../../DashboardPage/DashboardMenu";
import DynamicTable from "components/Tables/DynamicTable";
import { Checkbox, Typography } from "@material-tailwind/react";
import OwnerDetailsPage from "./OwnerDetailsPage";
import { FaHome } from "react-icons/fa";
import Loading from "components/Loader/Loading";

interface Client {
  id: number;
  name: string;
  lastName: string;
  validation: string;
  sendMessage: boolean;
  User: {
    id: number;
    email: string;
    isOwner?: boolean;
  };
}

export interface ListOwnersPageProps {
  className?: string;
}

const ListOwnersPage: FC<ListOwnersPageProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const {
    owners: ownerList,
    totalCount,
    totalPages,
    currentPage,
    loading,
    error,
  } = useAppSelector(
    (state) =>
      state?.auth?.allOwners || {
        owners: [],
        totalCount: 0,
        totalPages: 0,
        currentPage: 1,
        loading: "idle",
      }
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchLoading(true);
      await dispatch(adminGetAllOwners({ page, searchTerm: debouncedSearchTerm }));
      setIsSearchLoading(false);
    };
    fetchData();
  }, [dispatch, page, debouncedSearchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showClientDetails, setShowClientDetails] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteModalOpen = (client: Client) => {
    setSelectedClient(client);
    setShowClientDetails(false);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedClient && selectedClient.User?.id !== undefined) {
      setDeleting(true);
      await dispatch(deleteUser(selectedClient.User.id));
      setDeleting(false);
      setDeleteModalOpen(false);
      await dispatch(adminGetAllOwners({ page, searchTerm: debouncedSearchTerm }));
    }
  };

  const handleEyeIconClick = (client: Client) => {
    setSelectedClient(client);
    setShowClientDetails(true);
    setDeleteModalOpen(false);
  };

  const handleIsConversation = async (itemId: number, sendMessage: boolean) => {
    await dispatch(disableSendMessages({ clientId: itemId, status: !sendMessage })).then(() => {
      dispatch(adminGetAllOwners({ page, searchTerm: debouncedSearchTerm }));
    });
  };

  const columns = [
    {
      key: "name",
      label: "Nom et prénom",
      render: (item: Client) => <span>{`${item.lastName} ${item.name}`}</span>,
    },
    {
      key: "conversation",
      label: "Conversation",
      render: (item: Client) => (
        <Checkbox
          label={<Typography className="text-dark dark:text-white">Conversation</Typography>}
          checked={item.sendMessage}
          onChange={() => handleIsConversation(item.id, item.sendMessage)}
        />
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (item: Client) => (
        <div className="flex space-x-3">
          <i
            className="text-2xl text-blue-500 cursor-pointer las la-eye hover:text-blue-700"
            onClick={() => handleEyeIconClick(item)}
            aria-label={`Voir ${item.lastName} ${item.name}`}
            title="Consulter"
          ></i>
          <i
            className="text-2xl text-red-500 cursor-pointer las la-trash-alt hover:text-red-700"
            onClick={() => handleDeleteModalOpen(item)}
            aria-label={`Supprimer ${item.lastName} ${item.name}`}
            title="Supprimer"
          ></i>
        </div>
      ),
    },
  ];

  const handleTableAction = (action: string, item: Client) => {
    if (action === "view") {
      handleEyeIconClick(item);
    } else if (action === "delete") {
      handleDeleteModalOpen(item);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className="min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900" data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center space-x-3">
              <FaHome className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Liste des Propriétaires
              </h2>
            </div>
            <div className="relative w-full max-w-lg">
              <Input
                type="text"
                placeholder="Rechercher par nom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                aria-label="Rechercher des propriétaires"
              />
              <MagnifyingGlassIcon
                className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                  searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                }`}
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                  aria-label="Effacer la recherche"
                >
                  <XMarkIcon />
                </button>
              )}
            </div>
          </div>

          {(isSearchLoading || loading === "pending") && <Loading />}

          {error && (
            <div className="py-4 text-center text-red-600 dark:text-red-400">
              Erreur : {error.message || "Échec du chargement des propriétaires"}
            </div>
          )}

          {loading !== "pending" && !error && (
            <>
              {deleteModalOpen && (
                <DeleteModal
                  open={deleteModalOpen}
                  handleClose={() => setDeleteModalOpen(false)}
                  handleDelete={handleDeleteConfirm}
                  category={{
                    id: selectedClient?.User?.id || 0,
                    designation: `${selectedClient?.lastName} ${selectedClient?.name}` || "",
                  }}
                />
              )}

              {showClientDetails && !deleting && (
                <OwnerDetailsPage
                  open={true}
                  handleClose={() => setShowClientDetails(false)}
                  ownerData={selectedClient}
                />
              )}

              <DynamicTable
                columns={columns}
                data={ownerList}
                totalItems={totalCount || 0}
                currentPage={currentPage || 1}
                onPageChange={handlePageChange}
                onAction={handleTableAction}
                totalPages={totalPages}
                availableActions={["view", "delete"]}
              />
            </>
          )}
        </div>
      </DashboardMenu>
    </div>
  );
};

export default ListOwnersPage;
